/**
 * Modo de interfaz (Modo Fácil / Modo Pro).
 *
 * ⚠️ EL MODO ES POR PERSONA, NO POR ORGANIZACIÓN.
 * El estado activo vive en localStorage, con una clave que SIEMPRE incluye el id
 * del usuario autenticado. Nunca una clave global: en un carwash la app corre en
 * una tablet compartida, y una clave sin id haría que el modo de quien entró
 * antes le volteara la pantalla al siguiente. Por el mismo motivo el modo no se
 * guarda en business_settings — no es estado compartido.
 *
 * ⚠️ EL MODO NO ES UN PERMISO. Solo decide qué se DIBUJA. Quien autoriza de
 * verdad son las rutas, los PlanGate y la RLS. Ocultar algo aquí no bloquea
 * nada: la URL escrita a mano sigue abriendo.
 */

export const UI_MODE_FACIL = 'facil';
export const UI_MODE_PRO = 'pro';
export const UI_MODES = Object.freeze([UI_MODE_FACIL, UI_MODE_PRO]);

const STORAGE_PREFIX = 'cw_ui_mode:';

/**
 * Fecha de despliegue de Modo Fácil. Es la frontera del default:
 * quien ya existía antes sigue en Pro (cero cambio para las cuentas vivas);
 * quien se registre a partir de aquí arranca en Fácil.
 * Verificado al fijarla: los 8 owner/manager en producción son anteriores.
 */
export const MODO_FACIL_DEPLOY_DATE = '2026-07-23';

/**
 * Quién puede cambiar de modo. Dueño, gerente y superadmin; el empleado no
 * elige su propia vista. Superadmin entró el 2026-07-23 (decisión de producto):
 * la cuenta diaria del dueño real es superadmin en ambas tablas — no 'owner' —
 * y excluirla dejaba el toggle invisible justo para él. Su default sigue Pro.
 * Ojo: 'admin' NO está aquí. Es un valor real de employees.role, pero el rol
 * resuelto de un dueño es 'owner' — ver src/utils/roleAuthority.js.
 */
export const ROLES_UI_MODE_TOGGLE = Object.freeze(['owner', 'manager', 'superadmin']);

export const canToggleUiMode = (role) => ROLES_UI_MODE_TOGGLE.includes(role);

/** Clave por usuario. Devuelve null sin id — nunca se cae a una clave global. */
export const uiModeStorageKey = (userId) => (userId ? `${STORAGE_PREFIX}${userId}` : null);

const isValidMode = (value) => UI_MODES.includes(value);

/** Lee la preferencia guardada. null si no hay, es inválida, o no hay id. */
export const readStoredUiMode = (userId) => {
    const key = uiModeStorageKey(userId);
    if (!key) return null;
    try {
        const raw = localStorage.getItem(key);
        return isValidMode(raw) ? raw : null;
    } catch {
        return null; // Safari en privado, iOS con almacenamiento bloqueado
    }
};

export const writeStoredUiMode = (userId, mode) => {
    const key = uiModeStorageKey(userId);
    if (!key || !isValidMode(mode)) return false;
    try {
        localStorage.setItem(key, mode);
        return true;
    } catch {
        return false; // el modo sigue funcionando en memoria hasta recargar
    }
};

/**
 * Modo inicial cuando la persona todavía no ha elegido.
 * `orgDefault` es una semilla opcional por-organización (business_settings) para
 * cuentas nuevas; jamás se usa como estado activo.
 */
export const defaultUiModeFor = ({ createdAt, orgDefault } = {}) => {
    if (isValidMode(orgDefault)) return orgDefault;
    if (!createdAt) return UI_MODE_PRO; // sin fecha, no estrenamos nada
    return new Date(createdAt) >= new Date(MODO_FACIL_DEPLOY_DATE)
        ? UI_MODE_FACIL
        : UI_MODE_PRO;
};

/**
 * Roles cuya vista es SIEMPRE Modo Fácil, sin interruptor.
 *
 * No es lo mismo que ROLES_UI_MODE_TOGGLE: ahí están los que ELIGEN. El
 * empleado no elige — igual que antes — pero su vista pasa a ser la simple.
 * Antes caía en Pro por descarte, no por decisión: la regla era "quien no
 * puede cambiar de modo se queda en Pro", pensada para otra cosa. Medido en
 * Express, el empleado usa dos acciones de la decena que Pro le pinta
 * (42% de los "iniciar" del negocio y 29% de los "listo", cero cobros), así
 * que la pantalla completa era ruido para el único rol que no podía apagarlo.
 */
export const ROLES_ALWAYS_FACIL = Object.freeze(['employee']);

export const alwaysFacil = (role) => ROLES_ALWAYS_FACIL.includes(role);

/**
 * Modo efectivo. Orden: rol clavado en Fácil → elección guardada de la persona
 * → semilla de la org → regla de fecha. Quien no elige ni está clavado en
 * Fácil, se queda en Pro.
 */
export const resolveUiMode = ({ userId, role, createdAt, orgDefault } = {}) => {
    if (alwaysFacil(role)) return UI_MODE_FACIL;
    if (!canToggleUiMode(role)) return UI_MODE_PRO;
    return readStoredUiMode(userId) ?? defaultUiModeFor({ createdAt, orgDefault });
};

export const otherUiMode = (mode) => (mode === UI_MODE_FACIL ? UI_MODE_PRO : UI_MODE_FACIL);
